import type { ProductLabelFields } from './productLabels';

/** Variant enriched with selectedOptions — returned by fetchProductByHandle */
export interface ShopifyVariantFull {
  id: string;
  title: string;
  availableForSale: boolean;
  price: { amount: string; currencyCode: string };
  selectedOptions: Array<{ name: string; value: string }>;
}

export interface ShopifyProductFull extends ProductLabelFields {
  id: string;
  title: string;
  handle: string;
  descriptionHtml: string;
  /** Plain-text product description returned by the catalog API. */
  description?: string;
  tags?: string[];
  images: { nodes: Array<{ url: string; altText: string | null }> };
  variants: { nodes: ShopifyVariantFull[] };
  shortDescription?: { value: string } | null;
  collections?: { nodes: Array<{ handle: string; title?: string }> };
}

/** In-memory product cache — persists for the lifetime of the tab. */
const _productCache = new Map<string, ShopifyProductFull>();

/** Synchronously return a cached product (or undefined if not yet fetched). */
export function getCachedProduct(handle: string): ShopifyProductFull | undefined {
  return _productCache.get(handle);
}

/** Seed the tab cache from the verified product payload serialized by prerender. */
export function seedCachedProduct(product: ShopifyProductFull): void {
  if (!product.handle) return;
  _productCache.set(product.handle, normalizeProduct(product));
}

/**
 * Strona produktu odczytuje `.images.nodes`/`.variants.nodes` w kilkunastu
 * miejscach zakładając (zgodnie z typem ShopifyProductFull), że te pola
 * zawsze istnieją. Odpowiedź API jest jednak tylko rzutowana (`as`), bez
 * walidacji w runtime — jeśli backend kiedykolwiek zwróci produkt z brakującym
 * `images`/`variants` (błąd API, częściowa odpowiedź Shopify), rzutowanie
 * "się uda", a strona padnie dopiero głęboko w renderze. Normalizacja w
 * jednym miejscu, zanim produkt trafi do cache/stanu, jest prostsza i
 * pewniejsza niż zabezpieczanie każdego z tych miejsc osobno.
 */
export function normalizeProduct(p: ShopifyProductFull): ShopifyProductFull {
  return {
    ...p,
    images: p.images?.nodes ? p.images : { nodes: [] },
    variants: p.variants?.nodes ? p.variants : { nodes: [] },
  };
}
